import keymirror from 'keymirror';
import { request } from 'js/api/axios';
import { callApi } from '../api';
import { SCHEMAS } from 'js/api/schemas';
import { spreadApiKeys, extractApiKeys } from '../utils';
import { union } from 'lodash';

export const types = keymirror({
  ...spreadApiKeys('LOAD_MANAGED_PRODUCT'),
  ...spreadApiKeys('LOAD_MANAGED_PRODUCTS'),
  ...spreadApiKeys('CREATE_MANAGED_PRODUCT'),
  ...spreadApiKeys('UPDATE_MANAGED_PRODUCT'),
  ...spreadApiKeys('LINK_MANAGED_PRODUCT_CAPABILITY'),
  ...spreadApiKeys('UNLINK_MANAGED_PRODUCT_CAPABILITY'),
});

export function listManagedProducts(queryId = null) {
  return (dispatch) => {
    return callApi(
      {
        types: extractApiKeys(types.LOAD_MANAGED_PRODUCTS, types),
        request: (_data, token, currentWorkspaceId) =>
          request.get(
            `/api/workspaces/${currentWorkspaceId}/managed-products`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
        schema: SCHEMAS.MANAGED_PRODUCT_ARRAY,
        meta: { queryId },
      },
      dispatch
    );
  };
}

export function loadManagedProduct(id, queryId = null) {
  return (dispatch) => {
    return callApi(
      {
        types: extractApiKeys(types.LOAD_MANAGED_PRODUCT, types),
        request: (_data, token, currentWorkspaceId) =>
          request.get(
            `/api/workspaces/${currentWorkspaceId}/managed-products/${id}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
        schema: SCHEMAS.MANAGED_PRODUCT,
        meta: { queryId },
      },
      dispatch
    );
  };
}

export function createManagedProduct(managedProduct) {
  return (dispatch) => {
    return callApi(
      {
        types: extractApiKeys(types.CREATE_MANAGED_PRODUCT, types),
        request: (data, token, currentWorkspaceId) =>
          request.post(
            `/api/workspaces/${currentWorkspaceId}/managed-products`,
            data,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
        data: { managed_product: managedProduct },
        schema: SCHEMAS.MANAGED_PRODUCT,
      },
      dispatch
    );
  };
}

export function updateManagedProduct(patch) {
  const { id, ...rest } = patch;
  return (dispatch) => {
    return callApi(
      {
        types: extractApiKeys(types.UPDATE_MANAGED_PRODUCT, types),
        request: (data, token, currentWorkspaceId) =>
          request.put(
            `/api/workspaces/${currentWorkspaceId}/managed-products/${id}`,
            data,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
        data: { managed_product: rest },
        meta: { patch: { managedProducts: { [id]: rest } } },
        schema: SCHEMAS.MANAGED_PRODUCT,
      },
      dispatch
    );
  };
}

export function linkCapability(managedProductId, capabilityId) {
  return (dispatch) => {
    return callApi(
      {
        types: extractApiKeys(types.LINK_MANAGED_PRODUCT_CAPABILITY, types),
        request: (_data, token, currentWorkspaceId) =>
          request.post(
            `/api/workspaces/${currentWorkspaceId}/managed-products/${managedProductId}/capabilities/${capabilityId}`,
            {},
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
        schema: SCHEMAS.MANAGED_PRODUCT,
        meta: {
          patch: {
            managedProducts: {
              [managedProductId]: (productState) => {
                return {
                  ...productState,
                  capability_ids: union(productState?.capability_ids ?? [], [
                    capabilityId,
                  ]),
                };
              },
            },
          },
        },
      },
      dispatch
    );
  };
}

export function unlinkCapability(managedProductId, capabilityId) {
  return (dispatch) => {
    return callApi(
      {
        types: extractApiKeys(types.UNLINK_MANAGED_PRODUCT_CAPABILITY, types),
        request: (_data, token, currentWorkspaceId) =>
          request.delete(
            `/api/workspaces/${currentWorkspaceId}/managed-products/${managedProductId}/capabilities/${capabilityId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
        schema: SCHEMAS.MANAGED_PRODUCT,
        meta: {
          patch: {
            managedProducts: {
              [managedProductId]: (productState) => ({
                ...productState,
                capability_ids: (productState?.capability_ids ?? []).filter(
                  (c) => c !== capabilityId
                ),
                capabilities: (productState?.capabilities ?? []).filter(
                  (c) => c.id !== capabilityId
                ),
              }),
            },
          },
        },
      },
      dispatch
    );
  };
}

export function linkCapabilities(managedProductId, capabilityIds) {
  return (dispatch) => {
    return callApi(
      {
        types: extractApiKeys(types.LINK_MANAGED_PRODUCT_CAPABILITY, types),
        request: (data, token, currentWorkspaceId) =>
          request.post(
            `/api/workspaces/${currentWorkspaceId}/managed-products/${managedProductId}/capabilities`,
            data,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
        schema: SCHEMAS.MANAGED_PRODUCT,
        data: { action: 'create', capability_ids: capabilityIds },
        meta: {
          patch: {
            managedProducts: {
              [managedProductId]: (productState) => {
                return {
                  ...productState,
                  capability_ids: union(
                    productState?.capability_ids ?? [],
                    capabilityIds
                  ),
                };
              },
            },
          },
        },
      },
      dispatch
    );
  };
}

export function unlinkCapabilities(managedProductId, capabilityIds) {
  return (dispatch) => {
    return callApi(
      {
        types: extractApiKeys(types.LINK_MANAGED_PRODUCT_CAPABILITY, types),
        request: (data, token, currentWorkspaceId) =>
          request.post(
            `/api/workspaces/${currentWorkspaceId}/managed-products/${managedProductId}/capabilities`,
            data,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
        schema: SCHEMAS.MANAGED_PRODUCT,
        data: { action: 'delete', capability_ids: capabilityIds },
        meta: {
          patch: {
            managedProducts: {
              [managedProductId]: (productState) => {
                return {
                  ...productState,
                  capability_ids: (productState?.capability_ids ?? []).filter(
                    (c) => !capabilityIds.includes(c)
                  ),
                  capabilities: (productState?.capabilities ?? []).filter(
                    (c) => !capabilityIds.includes(c.id)
                  ),
                };
              },
            },
          },
        },
      },
      dispatch
    );
  };
}
